import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  ApiToken,
  CreateApiTokenRequest,
  CreateApiTokenResponse,
  DecryptApiTokenResponse,
} from '@app/core/interfaces/user/api-token.interface';
import { UserApiTokenService } from '@app/core/services/http/user-api-token.service';
import { User } from '@interfaces/a-organizar/user';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { TableModule } from 'primeng/table';
import { TabViewComponent } from '@src/app/shared/components/tab-view/tab-view.component';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';
import {
  API_TOKEN_VALIDITY_PRESETS,
  ApiTokenStatus,
  ApiTokenValidityPreset,
  apiTokenStatus,
  describeApiError,
  expirationFromPreset,
} from './api-token-display.util';

/** Aba da modal: lista de tokens ou formulário de token novo. */
type ApiTokensTab = 'list' | 'new';

/**
 * Modal de tokens de API de um usuário (Administração › Usuários › ícone de chave).
 *
 * Lista, cria, copia e revoga os tokens **do usuário da linha**, não de quem está logado: a criação
 * vai para `POST users/api-tokens/{userId}` (card 3697). O cabeçalho mostra para quem é o token, e o
 * formulário repete isso antes de gerar.
 */
@Component({
  selector: 'app-modal-api-tokens',
  templateUrl: './modal-api-tokens.component.html',
  styleUrls: ['./modal-api-tokens.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    TabViewComponent,
    TableModule,
    TooltipModule,
  ],
})
export class ModalApiTokensComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @Input() user: User | null = null;
  @Output() actionCompleted = new EventEmitter<unknown>();

  readonly validityPresets = API_TOKEN_VALIDITY_PRESETS;

  activeTab: ApiTokensTab = 'list';
  readonly apiTokens = signal<ApiToken[]>([]);
  readonly isLoadingTokens = signal(false);

  /** Cabeçalho das abas; a de tokens mostra quantos ainda valem (sem número quando nenhum). */
  readonly tabItems = computed<TabViewItem[]>(() => [
    { title: 'Tokens', count: this.countActive(this.apiTokens()), tabindex: 0, content: '' },
    { title: 'Novo token', tabindex: 1, content: '' },
  ]);
  selectedUserId: string | null = null;
  copyingTokens = new Set<string>();
  revokingTokens = new Set<string>();

  createTokenForm: FormGroup;
  validityPreset: ApiTokenValidityPreset = '10y';
  isCreatingToken = false;
  createdToken: string | null = null;
  createdTokenName = '';
  minDate: Date;

  constructor(
    translateService: TranslateService,
    private userApiTokenService: UserApiTokenService,
    private toast: ToastHelperService,
    private confirmationService: ConfirmationService,
    private dateFormatterService: DateFormatterService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    super(translateService);

    this.minDate = this.dateFormatterService.getTodayDate();
    this.createTokenForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      expirationDate: [this.presetExpiration('10y'), [Validators.required]],
    });
  }

  /** Abre a modal para o usuário informado (ou o `user` já atribuído) e carrega os tokens dele. */
  open(userId?: string): void {
    this.selectedUserId = userId || this.user?.id || null;
    this.activeTab = 'list';
    this.apiTokens.set([]);
    this.resetCreateTokenForm();

    if (this.selectedUserId) {
      this.loadApiTokens(true);
    }

    this.dialogRef?.open('', '', '', 'Cancel', '', 'modal-api-tokens', false, true);
  }

  /** Nome de quem vai receber o token, para o cabeçalho e os avisos. */
  get userName(): string {
    if (!this.user) return '';
    return this.user.name || [this.user.firstName, this.user.surName].filter(Boolean).join(' ');
  }

  /** Iniciais do avatar do cabeçalho. */
  get userInitials(): string {
    const parts = this.userName.trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  /** Tokens que ainda valem (nem revogados nem expirados). */
  private countActive(tokens: ApiToken[]): number {
    return tokens.filter(token => !this.getTokenStatus(token).inactive).length;
  }

  /**
   * Carrega os tokens do usuário. Na abertura (`initial`), sem nenhum token a modal já mostra o
   * estado vazio com o botão de gerar o primeiro.
   */
  loadApiTokens(initial = false): void {
    if (!this.selectedUserId) return;

    this.isLoadingTokens.set(true);
    this.userApiTokenService
      .getApiTokens({ userId: this.selectedUserId })
      .pipe(
        catchError(error => {
          this.toast.showToastError(
            describeApiError(
              error,
              'Não foi possível carregar os tokens. Feche e abra a modal de novo.'
            )
          );
          return of(null);
        })
      )
      .subscribe((response: ApiToken[] | null) => {
        this.apiTokens.set(response || []);
        this.isLoadingTokens.set(false);
        if (initial) this.activeTab = 'list';
      });
  }

  /** Situação do token (etiqueta e cor), calculada a partir das datas que a API devolve. */
  getTokenStatus(token: ApiToken): ApiTokenStatus {
    return apiTokenStatus(
      token,
      this.dateFormatterService.parseDateTruncateTime(token.expirationDate),
      this.dateFormatterService.getTodayDate()
    );
  }

  /** Classe da etiqueta de status. */
  statusClass(token: ApiToken): string {
    return `api-tokens__tag api-tokens__tag--${this.getTokenStatus(token).tone}`;
  }

  /** Troca de aba; ao entrar em "Novo token" o formulário volta limpo. */
  selectTab(tab: ApiTokensTab | string | number | undefined): void {
    if (tab !== 'list' && tab !== 'new') return;
    this.activeTab = tab;
    if (tab === 'new') this.resetCreateTokenForm();
  }

  /** Atalho de validade: preenche a data de vencimento; "Outra data" deixa o calendário à mostra. */
  selectValidity(preset: ApiTokenValidityPreset): void {
    this.validityPreset = preset;
    if (preset !== 'custom') {
      this.expirationDateControl.setValue(this.presetExpiration(preset));
    }
  }

  /** Volta o formulário ao padrão (sem nome, 10 anos) e esconde o token recém-criado. */
  resetCreateTokenForm(): void {
    this.validityPreset = '10y';
    this.createTokenForm.reset({ name: '', expirationDate: this.presetExpiration('10y') });
    this.createdToken = null;
    this.createdTokenName = '';
  }

  /** Gera o token para o usuário da modal e mostra o valor para copiar. */
  createToken(): void {
    if (!this.isFormValid() || !this.selectedUserId) {
      this.createTokenForm.markAllAsTouched();
      return;
    }

    this.isCreatingToken = true;
    const formValue = this.createTokenForm.value;
    const request: CreateApiTokenRequest = {
      name: formValue.name.trim(),
      expirationDate: this.dateFormatterService.formatDateToYYYYMMDD(formValue.expirationDate),
    };

    this.userApiTokenService
      .createApiToken(request, this.selectedUserId)
      .pipe(
        catchError(error => {
          this.toast.showToastError(
            describeApiError(error, 'Não foi possível gerar o token. Tente de novo em instantes.')
          );
          return of(null);
        })
      )
      .subscribe((response: CreateApiTokenResponse | null) => {
        this.isCreatingToken = false;
        this.cdr.markForCheck();
        if (!response) return;

        this.createdToken = response.plainTextToken;
        this.createdTokenName = response.token?.name || request.name;
        this.toast.showToastSuccess(`Token criado para ${this.userName}.`);
        this.loadApiTokens();
      });
  }

  /** Pede confirmação e revoga o token; quem usa o token perde o acesso na hora. */
  revokeToken(token: ApiToken): void {
    this.confirmationService
      .confirm(
        `Revogar o token "${token.name}"?`,
        'Quem usa esse token perde o acesso na hora. Não dá para desfazer: para voltar, gere um token novo.',
        'danger',
        'Cancelar',
        'Revogar token'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;

        this.revokingTokens.add(token.id);
        this.userApiTokenService
          .revokeApiToken(token.id)
          .pipe(
            catchError(error => {
              this.toast.showToastError(
                describeApiError(error, 'Não foi possível revogar o token. Tente de novo.')
              );
              return of(undefined);
            })
          )
          .subscribe(result => {
            this.revokingTokens.delete(token.id);
            this.cdr.markForCheck();
            if (result === undefined) return;
            this.toast.showToastSuccess(`Token "${token.name}" revogado.`);
            this.loadApiTokens();
          });
      });
  }

  /** Copia o token recém-criado. */
  copyCreatedToken(): void {
    if (this.createdToken) this.writeToClipboard(this.createdToken);
  }

  /** Busca o valor do token na API e copia. */
  copyTokenFromTable(token: ApiToken): void {
    if (this.copyingTokens.has(token.id)) return;

    this.copyingTokens.add(token.id);
    this.userApiTokenService
      .decryptApiToken(token.id)
      .pipe(
        catchError(error => {
          this.toast.showToastError(
            describeApiError(error, 'Não foi possível copiar o token. Tente de novo.')
          );
          return of(null);
        })
      )
      .subscribe((response: DecryptApiTokenResponse | null) => {
        this.copyingTokens.delete(token.id);
        this.cdr.markForCheck();
        if (response?.token) this.writeToClipboard(response.token);
      });
  }

  isCopyingToken(tokenId: string): boolean {
    return this.copyingTokens.has(tokenId);
  }

  isRevokingToken(tokenId: string): boolean {
    return this.revokingTokens.has(tokenId);
  }

  isFormValid(): boolean {
    return this.createTokenForm.valid;
  }

  get nameControl(): FormControl {
    return this.createTokenForm.get('name') as FormControl;
  }

  get expirationDateControl(): FormControl {
    return this.createTokenForm.get('expirationDate') as FormControl;
  }

  /** Data de vencimento escolhida, para o texto "Expira em ...". */
  get expirationDate(): Date | null {
    return this.expirationDateControl.value || null;
  }

  /** A ação principal da modal é gerar o token; o resto é feito na própria lista. */
  submitAction(): void {
    this.createToken();
  }

  onClose(): void {
    this.dialogRef?.close();
    this.actionCompleted.emit();
  }

  /** Vencimento de um atalho, contado a partir de hoje. */
  private presetExpiration(preset: ApiTokenValidityPreset): Date | null {
    return expirationFromPreset(preset, this.dateFormatterService.getTodayDate());
  }

  /** Copia o texto e avisa o resultado. */
  private writeToClipboard(value: string): void {
    navigator.clipboard
      .writeText(value)
      .then(() => this.toast.showToastSuccess('Token copiado para a área de transferência.'))
      .catch(() =>
        this.toast.showToastError(
          'O navegador bloqueou a cópia. Permita o acesso à área de transferência e tente de novo.'
        )
      );
  }
}
